import "../styles/job.css";
import { lazy, Suspense } from "react";

import AgenticAIHero from "./AgenticAIHero";
import AgenticAIJobRoles from "./AgenticAIJobRoles";

/* 🔥 Lazy-load heavy sections to avoid blocking navigation */
const Demand = lazy(() => import("../pages/AgenticAIDemand.js"));
const FeaturesSection = lazy(() => import("../pages/FeaturesSection"));
const ScrollingFeedback = lazy(() => import("../pages/ScrollingFeedback"));

export default function AgenticAIOverview() {
  return (
    <>
      {/* ================= HERO BANNER ================= */}
      <AgenticAIHero />

      {/* ================= MAIN CONTENT ================= */}
      <section className="genai-page">

        {/* PAGE INTRO */}
        <header className="genai-hero">
          <h1>Agentic AI Career Track – Build Autonomous AI Systems</h1>
          <p className="subtitle">
            Learn to Design Intelligent AI Agents that Plan, Reason, and Perform Tasks
          </p>
        </header>

        {/* PROGRAM OVERVIEW */}
        <section className="genai-section">
          <p>
            The Agentic AI Career Track focuses on learning how to build
            intelligent systems that can act autonomously and make decisions
            to complete tasks. It is based on modern Artificial Intelligence
            concepts where AI agents can plan, reason, and interact with tools
            or external systems.
          </p>

          <p>
            In this program, learners understand how to design AI agents that
            solve problems, automate workflows, and interact with users or
            software platforms. It covers technologies such as Large Language
            Models (LLMs), automation frameworks, and multi-agent systems to
            build advanced AI assistants and intelligent applications.
          </p>
        </section>

        {/* JOB ROLES */}
        <AgenticAIJobRoles />

        {/* LEARNING OUTCOMES */}
        <section className="genai-section">
          <h2>Key Learning Outcomes</h2>
          <ul className="outcome-list">
            <li>Understand the fundamentals of Artificial Intelligence and intelligent AI agents</li>
            <li>Design and build autonomous AI agents capable of planning and performing tasks</li>
            <li>Learn Large Language Models (LLMs) and AI agent frameworks</li>
            <li>Integrate AI agents with tools, APIs, and external data sources</li>
            <li>Understand multi-agent systems and automated workflows</li>
            <li>Develop AI-powered assistants and intelligent automation applications</li>
          </ul>
        </section>

        {/* WHO SHOULD ATTEND */}
        <section className="genai-section">
          <h2>Who Should Attend</h2>
          <ul className="outcome-list">
            <li>Students and fresh graduates interested in Artificial Intelligence</li>
            <li>Software developers who want to build AI agents and intelligent systems</li>
            <li>Machine learning or data science professionals expanding into AI automation</li>
            <li>Technology enthusiasts interested in autonomous systems and AI assistants</li>
            <li>Anyone curious about building AI-powered automation tools</li>
          </ul>

          <p className="salary-note">
            Basic programming knowledge in Python or JavaScript is helpful but not mandatory.
          </p>
        </section>

        {/* CAREER PATHWAYS */}
        <section className="genai-section">
          <h2>Career Pathways, Job Roles & Salary Range in India</h2>

          <div className="salary-table-wrapper">
            <table className="salary-table">
              <thead>
                <tr>
                  <th>Job Role</th>
                  <th>*Entry Salary</th>
                  <th>*Mid-Level Salary</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>Agentic AI Engineer</td>
                  <td>₹6 – 12 LPA</td>
                  <td>₹12 – 25 LPA</td>
                </tr>

                <tr>
                  <td>AI Agent Developer</td>
                  <td>₹6 – 11 LPA</td>
                  <td>₹12 – 22 LPA</td>
                </tr>

                <tr>
                  <td>LLM Application Developer</td>
                  <td>₹7 – 14 LPA</td>
                  <td>₹15 – 30 LPA</td>
                </tr>

                <tr>
                  <td>Machine Learning Engineer</td>
                  <td>₹6 – 12 LPA</td>
                  <td>₹12 – 24 LPA</td>
                </tr>

                <tr>
                  <td>AI Automation Engineer</td>
                  <td>₹7 – 13 LPA</td>
                  <td>₹14 – 28 LPA</td>
                </tr>

                <tr>
                  <td>AI Solutions Architect</td>
                  <td>₹10 – 18 LPA</td>
                  <td>₹20 – 40+ LPA</td>
                </tr>
              </tbody>
            </table>

            <p className="salary-note">
              *Salary ranges vary based on skills, experience, projects,
              and demand in the AI industry.
            </p>
          </div>
        </section>

        {/* PROJECT HIGHLIGHT */}
        <section className="genai-section highlight">
          <h2>Project Highlights</h2>
          <p>
            <strong>Autonomous AI Task Agent:</strong> An intelligent system
            built using Artificial Intelligence that can plan, reason, and
            execute tasks automatically. The project integrates Large
            Language Models, external tools, and workflow automation to
            complete user requests such as information retrieval, data
            analysis, and task scheduling. It demonstrates how AI agents
            interact with APIs and external systems to accomplish complex
            tasks autonomously.
          </p>
        </section>

        {/* ================= LAZY LOADED SECTIONS ================= */}
        <Suspense fallback={<div style={{ padding: 40 }}>Loading insights…</div>}>
          <Demand />
          <FeaturesSection />
          <ScrollingFeedback />
        </Suspense>

      </section>
    </>
  );
}